import { DataGridPro, GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { PurePtr, useLocalStoragePtr } from "@pure-ptr/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { set } from "../../store/actions/pages";
import { RootState } from "../../store/reducers";
import { useWindowEvent } from "../../util";
import { CombinedFilter, getPaginationFilter } from "../../util/pagination";
import { Field } from "../dataTableHeader/DataTableHeader";
import { AdvancedTableFilters } from "../pagedTable";
import { PopoverLink } from "../popoverLink";
import { SortOptions, PaginationFilters, createActionsField, dataGridStyles, TableFilterState } from "./common";
import { translateLegacyFields } from "./LegacyGridColumns";
import { toLocalStorage,fromSessionStorage } from "../../util/storage";
interface XPagedDataGridProps {
    selectedIdsPtr?: PurePtr<readonly (string|number)[]>;
    loading: boolean;
    data: any[];
    showSearchFilters:boolean;
    name: string;
    paginationFilters: PaginationFilters;
    sortPtr: PurePtr<SortOptions>;
    fields: Field[];
    defaultSort: SortOptions;
    criteriasSaveEnabled: boolean;
    paginationPtr: PurePtr<PaginationFilters["pagination"]>;
    actions?: (params: any) => React.ReactNode;
    defaultHiddenFields?: string[];
    localStorageKey?: string;
    defaultCustomFilter?: any;
    filtersPtr: PurePtr<TableFilterState['filters']>;
    refreshCriteriaList?: () => void;
    selectedCriteria?: any;
}

export function XPagedDataGrid({ 
    filtersPtr, 
    selectedCriteria,
    refreshCriteriaList, defaultHiddenFields, paginationPtr, sortPtr, defaultCustomFilter, selectedIdsPtr, loading, data, name, fields, showSearchFilters, defaultSort, criteriasSaveEnabled, actions
}: XPagedDataGridProps) {
    const apiRef = useGridApiRef();
    const allColumnsWidths =()=> fromSessionStorage(`${name}-column-widths`) || {};

    const columnDefs:any = useMemo(() => [
        ...translateLegacyFields(fields),
        ...(actions ? [createActionsField(actions)] : [])
    ], [fields, actions]);

    // avoid default resizing those columns having resized width in sessionStorage
    const autosizeOptions = useMemo(() => {
        const columnWidths = allColumnsWidths();
       return {
        columns : columnDefs.map( x => x.field ).filter( x => x !== 'actions' && x?.field &&  !columnWidths[x] ),
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    }}, [ fields ]);

    const initialColumnVisibility = useCallback(() =>
        Object.fromEntries( defaultHiddenFields.map( x => [ x, false ]))
    , [defaultHiddenFields]);

    const columnVisibilityModelPtr = useLocalStoragePtr( name + '-visibility', initialColumnVisibility ); 
    toLocalStorage( name + '-visibility', columnVisibilityModelPtr.value );

    const columnsOrderPtr = useLocalStoragePtr( name + '-order', () => columnDefs.map( ( x, i ) => i ) );

    const orderedColumns = useMemo(() => 
        columnsOrderPtr.value.map( i => columnDefs[i] ),
    [ columnsOrderPtr.value, columnDefs ]);

    const resizeColumns = useCallback(() => {
        if( !loading && data.length > 0 ){
            // `sleep`/`setTimeout` is required because update rows is an
            // async function throttled to avoid choking on frequent changes.
            setTimeout(() => {
                apiRef.current.autosizeColumns( autosizeOptions )
            }, 0);
        }
    }, [ apiRef.current, loading, data ]);


    // saving resized widths in sessionStorage so they are temporary
    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const newWidths = {
            ...allColumnsWidths(),
            [colDef.field]: width,
        };
        sessionStorage.setItem(
            `${name}-column-widths`,
            JSON.stringify(newWidths)
        );
    };

    useEffect( resizeColumns, [ resizeColumns ]);
    useWindowEvent( 'resize', resizeColumns, 100 );
    return <>
        <DataGridPro
            onColumnWidthChange={handleColumnResize}
            apiRef={apiRef}
            autosizeOptions={autosizeOptions}
            localeText={{ noRowsLabel: "No Result Found" }}
            density="compact"
            columns={orderedColumns?.filter((item)=>item?.field)}
            rows={data}
            disableColumnFilter
            loading={loading}
            sortingOrder={['desc', 'asc']}
            rowSelectionModel={selectedIdsPtr?.value}
            onRowSelectionModelChange={x => selectedIdsPtr.set(x)}

            checkboxSelection={ Boolean(selectedIdsPtr) } 
            disableRowSelectionOnClick

            columnVisibilityModel={columnVisibilityModelPtr.value}
            onColumnVisibilityModelChange={x => columnVisibilityModelPtr.set(x)}

            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={ toPaginationModel( paginationPtr.value ) }

            paginationMode="server"
            onPaginationModelChange={ x =>
                paginationPtr.update( prev => ({
                    ...prev, 
                    currentPage: x.page + 1, 
                    recordsPerPage: x.pageSize 
                }) )
            }
            rowCount={paginationPtr.value.totalRecords ?? 0}

            onColumnOrderChange={ ({ oldIndex, targetIndex, column }) => {
                columnsOrderPtr.update( prev => {
                    const newOrder = [...prev];
                    const [ moved ] = newOrder.splice( oldIndex - 1, 1 );
                    newOrder.splice( targetIndex - 1, 0, moved );
                    return newOrder;
                });
            }}

            sortingMode="server"
            sortModel={ sortPtr.value.map(x => ({
                field: x.id,
                sort: x.direction as any,
            }))}
            onSortModelChange={arr => 
                sortPtr.set(arr.map(x => ({ id: x.field, direction: x.sort })))
            }
            
            slotProps={ slotProps}

            sx={ dataGridStyles }
        />

        <AdvancedTableFilters
            showSearchFilters={showSearchFilters}
            name={name}
            fields={fields}
            refreshCriteriaList={refreshCriteriaList}
            defaultCustomFilter={defaultCustomFilter}
            selectedCriteria={selectedCriteria}
            filtersPtr={ filtersPtr }
            defaultSort={defaultSort}
            criteriasSaveEnabled={criteriasSaveEnabled} />
    </>;
}

function toPaginationModel( { currentPage = 1, recordsPerPage, totalRecords = 0 } : PaginationFilters["pagination"] ) {
    return {
        pageSize: recordsPerPage,
        page: Math.min( Math.floor( totalRecords / recordsPerPage ) + 1, currentPage ) - 1,
    }
}

const slotProps = {
    columnsManagement: {
      disableShowHideToggle: true,
      getTogglableColumns( columns : GridColDef[] ) {
        return columns
            .filter( column => column.field !== 'actions' && column.field !== '__check__' )
            .map( column => column.field );
      }
    },
}